import { getUserDetails } from "./isSystemControllerUser";

/**
 * Permission codes shipped by the API in the login response (EN-4961).
 * Use the constants below rather than raw strings so a rename on the API
 * surfaces here as a type/compile error.
 */
export const PERMISSIONS = {
  ADMIN_REPORTS_READ: "admin-reports:read",
  AUDIT_LOGS_READ: "audit-logs:read",
  // EN-5129 per-page /admin permissions (<page-slug>:access). Granted per
  // user (not via the role) so page access can be chosen even among
  // SuperAdmins — each migrated Retool page adds its code here.
  BILLABLE_DEVICES_ACCESS: "billable-devices:access",
  ROLE_PERMISSIONS_ACCESS: "role-permissions:access",
  USER_ACCESS_ACCESS: "user-access:access",
  JUPYTER_INTEGRATION_ACCESS: "jupyter-integration:access",
  TMP_INTEGRATION_ACCESS: "tmp-integration:access",
  DEXCOM_INTEGRATION_ACCESS: "dexcom-integration:access",
  INTEGRATION_API_KEYS_ACCESS: "integration-api-keys:access",
  STAFF_ACCESS: "staff:access",
  WARDS_ACCESS: "wards:access",
  HOSPITALS_ACCESS: "hospitals:access",
  TENANTS_ACCESS: "tenants:access",
  TREATMENTS_ACCESS: "treatments:access",
  ONE_MSTS_ACCESS: "one-msts:access",
  SPIROMETRY_ACCESS: "spirometry:access",
  STUDIES_ACCESS: "studies:access",
  WARD_MEASUREMENT_TYPES_ACCESS: "ward-measurement-types:access",
  MEASUREMENTS_ACCESS: "measurements:access",
  EMED_BEAUMONT_ACCESS: "emed-beaumont:access",
  AI_PROMPT_TEMPLATES_ACCESS: "ai-prompt-templates:access",
  EMAIL_TEMPLATES_ACCESS: "email-templates:access",
  SURVEY_TRANSLATIONS_ACCESS: "survey-translations:access",
  SURVEYS_ACCESS: "surveys:access",
  ALERTS_ACCESS: "alerts:access",
  FIRMWARE_ACCESS: "firmware:access",
  CELLULAR_DEVICES_ACCESS: "cellular-devices:access",
  WORKFLOWS_ACCESS: "workflows:access",
  BASELINES_ACCESS: "baselines:access",
  PATIENTS_ACCESS: "patients:access",
  TRANSLATIONS_ACCESS: "translations:access",
  TRANSLATIONS_SYNC_ACCESS: "translations-sync:access",
  MEDICATIONS_ACCESS: "medications:access",
  QUARTZ_JOBS_ACCESS: "quartz-jobs:access",
  COGNITO_MIGRATION_ACCESS: "cognito-migration:access",
} as const;

export type IPermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Returns true when the authenticated user holds the given permission code.
 * Reads from the AUTH_USER_DETAILS localStorage key — populated by the
 * login flow in AuthContext, which serializes the whole `userDetails`
 * object (role, permissions, name, email) under that single key.
 * Returns false when no permissions are stored (e.g. legacy sessions
 * predating EN-4961, or unauthenticated state).
 */
export function hasPermission(code: IPermissionCode): boolean {
  const permissions = getUserDetails()?.permissions;
  return Array.isArray(permissions) && permissions.includes(code);
}
